import { useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import type { KeywordRow } from '@/data/keywords'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ContentBriefProps {
  keyword: KeywordRow
  onClear: () => void
}

type Status = 'idle' | 'loading' | 'success' | 'error'

// ---------------------------------------------------------------------------
// Markdown renderer — no external deps, covers headings/bold/italic/code/lists
// ---------------------------------------------------------------------------
function renderMarkdown(md: string): string {
  return md
    // Fenced code blocks
    .replace(/```[\s\S]*?```/g, (match) => {
      const inner = match.replace(/^```[^\n]*\n?/, '').replace(/\n?```$/, '')
      const escaped = inner.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return `<pre class="my-3 overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 font-mono text-xs leading-relaxed text-emerald-300 border border-slate-700"><code>${escaped}</code></pre>`
    })
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs text-slate-700 border border-slate-200">$1</code>')
    // H4
    .replace(/^#### (.+)$/gm, '<h4 class="mt-4 mb-1.5 text-sm font-semibold text-slate-700 tracking-tight">$1</h4>')
    // H3
    .replace(/^### (.+)$/gm, '<h3 class="mt-6 mb-2 text-base font-semibold text-slate-800 tracking-tight border-b border-slate-100 pb-1">$1</h3>')
    // H2
    .replace(/^## (.+)$/gm, '<h2 class="mt-8 mb-3 text-lg font-bold text-slate-900 tracking-tight">$1</h2>')
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="my-6 border-slate-200" />')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em class="italic text-slate-700">$1</em>')
    // Unordered list items
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-slate-700 leading-relaxed text-sm">$1</li>')
    // Ordered list items
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal text-slate-700 leading-relaxed text-sm">$1</li>')
    // Paragraphs (non-empty lines not already wrapped)
    .replace(/^(?!<[hplci]|<pre|<hr|<li|<strong|<em)(.+)$/gm, '<p class="text-sm leading-relaxed text-slate-600 mb-2">$1</p>')
    // Wrap consecutive <li> elements in <ul>
    .replace(/(<li[\s\S]+?<\/li>)(?=\s*(?!<li))/g, '<ul class="my-2 space-y-0.5">$1</ul>')
}

// ---------------------------------------------------------------------------
// Section skeleton for loading state
// ---------------------------------------------------------------------------
function SkeletonSection() {
  return (
    <div className="animate-pulse space-y-3 py-2">
      <div className="h-5 w-2/5 rounded bg-slate-200" />
      <div className="h-3 w-full rounded bg-slate-100" />
      <div className="h-3 w-5/6 rounded bg-slate-100" />
      <div className="h-3 w-4/5 rounded bg-slate-100" />
      <div className="mt-4 h-5 w-1/3 rounded bg-slate-200" />
      <div className="h-3 w-full rounded bg-slate-100" />
      <div className="h-3 w-3/4 rounded bg-slate-100" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Difficulty badge colour
// ---------------------------------------------------------------------------
function difficultyColor(d: number) {
  if (d <= 30) return 'bg-emerald-100 text-emerald-800'
  if (d <= 60) return 'bg-amber-100 text-amber-800'
  return 'bg-red-100 text-red-800'
}

// ---------------------------------------------------------------------------
// ContentBrief component
// ---------------------------------------------------------------------------
export default function ContentBrief({ keyword, onClear }: ContentBriefProps) {
  const [status, setStatus] = useState<Status>('idle')
  const [brief, setBrief]   = useState<string>('')
  const [copied, setCopied] = useState(false)
  const [error, setError]   = useState<string>('')

  // ── Generate brief ────────────────────────────────────────────────────────
  const generate = useCallback(async () => {
    setStatus('loading')
    setBrief('')
    setError('')

    try {
      const result = await lumi.functions.invoke('contentBrief', {
        method: 'POST',
        body: {
          keyword:    keyword.keyword,
          slug:       keyword.slug,
          category:   keyword.category,
          volume:     keyword.volume,
          difficulty: keyword.difficulty,
          intent:     keyword.intent,
          reason:     keyword.reason,
        },
      })

      const data = result as { brief?: string; error?: string }

      if (data.error) {
        setError(data.error)
        setStatus('error')
        return
      }

      setBrief(data.brief ?? '')
      setStatus('success')
    } catch (err: any) {
      const msg = err?.data?.error ?? err?.message ?? 'An unknown error occurred'
      setError(msg)
      setStatus('error')
    }
  }, [keyword])

  // ── Copy markdown to clipboard ────────────────────────────────────────────
  const copyMarkdown = useCallback(async () => {
    if (!brief) return
    try {
      await navigator.clipboard.writeText(brief)
      setCopied(true)
      setTimeout(() => setCopied(false), 2200)
    } catch {
      // fallback — select text
    }
  }, [brief])

  // ── Regenerate ────────────────────────────────────────────────────────────
  const regenerate = useCallback(() => {
    generate()
  }, [generate])

  return (
    <section className="mx-auto max-w-7xl px-4 pb-10 pt-4 sm:px-6">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">

        {/* ── Panel header ─────────────────────────────────────────────────── */}
        <div className="border-b border-slate-100 bg-slate-50 px-6 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">

            {/* Left: title + meta */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex h-5 items-center rounded-full bg-violet-100 px-2 text-[10px] font-bold uppercase tracking-widest text-violet-700">
                  Component C
                </span>
                <span className="text-xs font-medium text-slate-400">
                  AI Content Brief Generator
                </span>
              </div>
              <h2 className="mt-1.5 font-mono text-sm font-semibold text-slate-900 leading-snug truncate" title={keyword.keyword}>
                {keyword.keyword}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                <code className="font-mono text-slate-400">{keyword.slug}</code>
                <span className="text-slate-300">·</span>
                <span>{keyword.category}</span>
                <span className="text-slate-300">·</span>
                <span>~{keyword.volume.toLocaleString()} / mo</span>
                <span className="text-slate-300">·</span>
                <span className={`rounded-full px-1.5 py-0.5 font-semibold ${difficultyColor(keyword.difficulty)}`}>
                  KD {keyword.difficulty}
                </span>
                <span className="text-slate-300">·</span>
                <span className="italic">{keyword.intent}</span>
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex shrink-0 items-center gap-2">
              {status === 'success' && (
                <>
                  <button
                    type="button"
                    onClick={copyMarkdown}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                  >
                    {copied ? (
                      <>
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                          <path d="M2 7l3 3 6-6" stroke="#16a34a" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                          <rect x="4" y="4" width="7" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.4"/>
                          <path d="M2 9V2h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Copy Markdown
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={regenerate}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                      <path d="M11 2.5A5.5 5.5 0 1 0 12 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M12 2.5V5.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Regenerate
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={onClear}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-500 transition hover:border-slate-300 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              >
                ✕ Clear
              </button>
            </div>
          </div>
        </div>

        {/* ── Panel body ───────────────────────────────────────────────────── */}
        <div className="px-6 py-6">

          {/* IDLE: prompt user to generate */}
          {status === 'idle' && (
            <div className="flex flex-col items-center justify-center gap-5 py-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-50 border border-violet-100">
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
                  <path d="M13 3C7.477 3 3 7.477 3 13s4.477 10 10 10 10-4.477 10-10S18.523 3 13 3Z" stroke="#7c3aed" strokeWidth="1.6"/>
                  <path d="M9 13c0-2.21 1.79-4 4-4s4 1.79 4 4-1.79 4-4 4" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round"/>
                  <circle cx="13" cy="13" r="1.5" fill="#7c3aed"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Generate a full SEO &amp; Developer Brief
                </p>
                <p className="mt-1 max-w-md text-xs leading-relaxed text-slate-500">
                  Powered by <span className="font-mono font-medium text-slate-700">gemini-2.5-flash</span> via Lumi AI.
                  Produces SEO metadata, content architecture, calculator spec, and a link-building plan — all tailored to this exact keyword.
                </p>
              </div>
              <button
                type="button"
                onClick={generate}
                className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                  <path d="M7.5 1v13M1 7.5h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Generate AI Content Brief
              </button>
              <p className="text-[11px] text-slate-400">
                Typically takes 10–20 seconds · Uses your Lumi AI quota
              </p>
            </div>
          )}

          {/* LOADING: skeleton + spinner */}
          {status === 'loading' && (
            <div>
              <div className="mb-5 flex items-center gap-3 rounded-xl border border-violet-100 bg-violet-50 px-4 py-3">
                <svg className="animate-spin" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <circle cx="9" cy="9" r="7" stroke="#ddd6fe" strokeWidth="2.5"/>
                  <path d="M2 9a7 7 0 0 1 7-7" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
                <div>
                  <p className="text-xs font-semibold text-violet-800">Generating content brief…</p>
                  <p className="text-[11px] text-violet-600">
                    gemini-2.5-flash is building your SEO &amp; developer blueprint for &ldquo;{keyword.keyword}&rdquo;
                  </p>
                </div>
              </div>
              <SkeletonSection />
              <div className="mt-6">
                <SkeletonSection />
              </div>
            </div>
          )}

          {/* ERROR state */}
          {status === 'error' && (
            <div className="flex flex-col items-center gap-5 py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 border border-red-100">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="9" stroke="#dc2626" strokeWidth="1.6"/>
                  <path d="M11 7v5M11 15v.5" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-red-700">Generation failed</p>
                <p className="mt-1 max-w-sm text-xs leading-relaxed text-slate-500">{error}</p>
              </div>
              <button
                type="button"
                onClick={generate}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                Try again
              </button>
            </div>
          )}

          {/* SUCCESS: rendered markdown */}
          {status === 'success' && brief && (
            <div>
              {/* Stats bar */}
              <div className="mb-5 flex flex-wrap gap-3">
                {[
                  { label: 'Sections', value: '4', color: 'bg-violet-50 text-violet-700 border-violet-100' },
                  { label: 'Model',    value: 'gemini-2.5-flash', color: 'bg-slate-50 text-slate-600 border-slate-200' },
                  { label: 'Keyword',  value: keyword.category,   color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
                  { label: 'KD',       value: `${keyword.difficulty}/100`, color: `border ${difficultyColor(keyword.difficulty)}` },
                ].map((s) => (
                  <div key={s.label} className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium ${s.color}`}>
                    <span className="font-semibold">{s.value}</span>
                    <span className="opacity-60">{s.label}</span>
                  </div>
                ))}
              </div>

              {/* Rendered brief */}
              <div
                className="prose-sm max-w-none leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(brief) }}
              />

              {/* Bottom copy utility */}
              <div className="mt-8 flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-500">
                  Brief generated for <span className="font-mono font-medium text-slate-700">{keyword.slug}</span>
                </p>
                <button
                  type="button"
                  onClick={copyMarkdown}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                >
                  {copied ? '✓ Copied!' : '⎘ Copy Markdown'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
